import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

type ToastVariant = 'success' | 'error' | 'info';
export interface ToastItem {
  id: string;
  title: string;
  description?: string | undefined;
  variant?: ToastVariant | undefined;
}

interface ToastCtx {
  show: (t: Omit<ToastItem, 'id'> & { durationMs?: number }) => void;
  success: (title: string, description?: string) => void;
  error: (title: string, description?: string) => void;
}

const Ctx = createContext<ToastCtx | null>(null);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<ToastItem[]>([]);

  const remove = useCallback((id: string) => {
    setItems(prev => prev.filter(t => t.id !== id));
  }, []);

  const show = useCallback((t: Omit<ToastItem, 'id'> & { durationMs?: number }) => {
    const id = Math.random().toString(36).slice(2);
    const item: ToastItem = { id, title: t.title, description: t.description ?? undefined, variant: t.variant ?? undefined };
    setItems(prev => [...prev, item]);
    const ms = t.durationMs ?? 3000;
    window.setTimeout(() => remove(id), ms);
  }, [remove]);

  const value: ToastCtx = useMemo(() => ({
    show,
    success: (title: string, description?: string) => show({ title, description, variant: 'success' }),
    error: (title: string, description?: string) => show({ title, description, variant: 'error' }),
  }), [show]);

  return (
    <Ctx.Provider value={value}>
      {children}
      {/* Container responsivo (mobile: bottom sheet-like, desktop: canto inferior direito) */}
      <div className="fixed inset-x-0 bottom-0 px-4 pb-4 sm:inset-auto sm:right-4 sm:bottom-4 sm:left-auto sm:px-0 z-[10000] space-y-2">
        {items.map(t => (
          <div key={t.id} className={`bg-surface border border-default card rounded-lg px-4 py-3 shadow-lg animate-slide-up flex items-start gap-3 ${t.variant === 'success' ? 'ring-1 ring-green-500/30' : t.variant === 'error' ? 'ring-1 ring-red-500/30' : ''}`}>
            <i className={`ph ${t.variant === 'success' ? 'ph-check-circle text-green-500' : t.variant === 'error' ? 'ph-warning-circle text-red-500' : 'ph-info text-blue-500'} text-xl mt-0.5`}></i>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-zinc-900 dark:text-white truncate">{t.title}</p>
              {t.description && <p className="text-xs text-muted truncate">{t.description}</p>}
            </div>
            <button onClick={() => remove(t.id)} className="text-muted hover:text-zinc-900 dark:hover:text-white"><i className="ph ph-x"/></button>
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
};
